import { useMemo, useState } from 'react';
import AppLayout from '../components/AppLayout';
import { useSettings } from '../hooks/useSettings';
import { usePaging } from '../hooks/usePaging';
import { Paging } from '../components/ui';
import { useLedgerData } from '../hooks/useLedgerData';
import { DateRangeButton } from '../components/HeaderControls';
import { fmtMoney } from '../utils/currency';
import { generalLedger, computeDateWindow, TYPE_LABEL, type DateFilterKey } from '../utils/ledgerReports';
import type { LedgerAccountType } from '../types';

const SRC_LABEL: Record<string, string> = {
  purchase_invoice: 'Purchase', sales_invoice: 'Sale',
  ap_payment: 'Supplier payment', ar_payment: 'Customer receipt',
  broker_accrual: 'Broker commission', broker_payment: 'Broker payout',
  journal: 'Journal entry', expense: 'Expense', income: 'Income',
  opening_balance: 'Opening balance', opening_stock: 'Opening stock',
  purchase_expense: 'Purchase expense', sale_expense: 'Sales expense',
  purchase_return: 'Purchase return', sales_return: 'Credit note', purchase_discount: 'Purchase discount', sale_settle: 'Settled difference',
  supplier_advance: 'Supplier advance', supplier_advance_apply: 'Supplier advance applied',
  customer_advance: 'Customer advance', customer_advance_apply: 'Customer advance applied',
};
const srcLabel = (s: string) => SRC_LABEL[s] ?? s.replace(/_/g, ' ');

export default function GeneralLedgerPage() {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const { entries, accounts, loading } = useLedgerData();

  // Opened from Profit & Loss (a new tab): ?account=<id>&range=<key>&from=&to=
  // starts on that account and the same dates.
  const [urlInit] = useState(() => new URLSearchParams(globalThis.location?.search ?? ''));
  const [accountId, setAccountId] = useState(urlInit.get('account') ?? '');
  const [dateKey, setDateKey]     = useState<DateFilterKey>(() => {
    const r = urlInit.get('range');
    return r === 'this-month' || r === '3-months' || r === 'this-year' || r === 'all' || r === 'custom' ? r : 'this-year';
  });
  const [customFrom, setCustomFrom] = useState(urlInit.get('from') ?? '');
  const [customTo,   setCustomTo]   = useState(urlInit.get('to') ?? '');

  const sortedAccounts = useMemo(
    () => [...accounts].sort((a, b) => (a.code || 'zzz').localeCompare(b.code || 'zzz')),
    [accounts],
  );
  const account = accounts.find(a => a.id === accountId) ?? null;
  const window  = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);

  const gl = useMemo(() => {
    if (!account || !window) return null;
    return generalLedger(entries, account.id, account.normalBalance, window);
  }, [entries, account, window]);
  // 50 rows at a time; each row's Balance is already worked out over all rows.
  const pg = usePaging(gl?.rows ?? [], [accountId, dateKey, customFrom, customTo].join('|'));

  const accountPicker = (
    <select
      className="no-print tb-select"
      value={accountId}
      onChange={e => setAccountId(e.target.value)}
      aria-label="Account"
      style={{ minWidth: 240 }}
    >
      <option value="">Choose an account…</option>
      {(['asset', 'liability', 'equity', 'income', 'expense'] as LedgerAccountType[]).map(t => {
        const rows = sortedAccounts.filter(a => a.type === t);
        if (rows.length === 0) return null;
        return (
          <optgroup key={t} label={TYPE_LABEL[t]}>
            {rows.map(a => <option key={a.id} value={a.id}>{a.code ? `${a.code} — ` : ''}{a.name}</option>)}
          </optgroup>
        );
      })}
    </select>
  );

  return (
    <AppLayout
      title="General Ledger"
      subtitle="Every movement in one account, with a running balance"
      toolbar={<>
        {accountPicker}
        <DateRangeButton
          value={dateKey} onChange={setDateKey}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
      </>}
    >
      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : !account ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>Pick an account</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Choose one above to see its ledger.</div>
        </div>
      ) : !window ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px', fontSize: 13, color: 'var(--text-3)' }}>
          Choose a From and To date above.
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 150 }}>Type</th>
                  <th>Note</th>
                  <th style={{ textAlign: 'right', width: 130 }}>Debit</th>
                  <th style={{ textAlign: 'right', width: 130 }}>Credit</th>
                  <th style={{ textAlign: 'right', width: 140 }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: 'var(--surface-2)' }}>
                  <td colSpan={5} style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)' }}>Opening balance</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700 }}>{money(gl!.opening)}</td>
                </tr>
                {gl!.rows.length === 0 ? (
                  <tr><td colSpan={6} style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'center', padding: '20px 0' }}>
                    No movement in this period.
                  </td></tr>
                ) : pg.pageRows.map((r, i) => (
                  <tr key={r.entryId + i}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{r.date}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{srcLabel(r.sourceType)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.note || <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>{r.debit ? money(r.debit) : ''}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>{r.credit ? money(r.credit) : ''}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', textAlign: 'right', fontWeight: 600 }}>{money(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--surface-2)', borderTop: '2px solid var(--border)' }}>
                  <td colSpan={5} style={{ fontWeight: 700, fontSize: 12 }}>Closing balance</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12 }}>{money(gl!.closing)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <Paging page={pg.page} totalPages={pg.totalPages} total={pg.total} onChange={pg.setPage} noun="entry" />
        </div>
      )}
    </AppLayout>
  );
}
